<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { Button } from '@/Components/ui/button'
import { Textarea } from '@/Components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/Components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/Components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/Components/ui/alert-dialog'
import { usePage } from '@inertiajs/vue3'
import teacher from '@/routes/teacher'

const props = defineProps({
  contentId: {
    type: [Number, String],
    required: true
  },
  contentOwnerId: {
    type: [Number, String],
    required: true
  }
})

// Humanize dates (e.g., "2 hours ago", "just now")
const humanizeDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now - date) / 1000)

  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minute${Math.floor(seconds / 60) > 1 ? 's' : ''} ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} day${Math.floor(seconds / 86400) > 1 ? 's' : ''} ago`
  if (seconds < 2592000) return `${Math.floor(seconds / 604800)} week${Math.floor(seconds / 604800) > 1 ? 's' : ''} ago`
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)} month${Math.floor(seconds / 2592000) > 1 ? 's' : ''} ago`
  return `${Math.floor(seconds / 31536000)} year${Math.floor(seconds / 31536000) > 1 ? 's' : ''} ago`
}

const comments = ref([])
const loading = ref(false)
const submitting = ref(false)
const newComment = ref('')
const replyingTo = ref(null)
const pagination = ref({
  current_page: 1,
  last_page: 1,
  total: 0,
  has_more: false
})
const currentUser = usePage().props.auth.user

// Check if current user can delete a comment
const canDeleteComment = (comment) => {
  // User can delete if they are the comment owner OR the content owner
  return currentUser.id === comment.user.id || currentUser.id === props.contentOwnerId
}

// Fetch comments
const fetchComments = async (page = 1, append = false) => {
  loading.value = true
  try {
    const response = await axios.get(teacher.content.comments.index(props.contentId).url, {
      params: { page }
    })

    // Laravel paginator returns { data, current_page, last_page, total, etc. }
    if (append) {
      comments.value = [...comments.value, ...response.data.data]
    } else {
      comments.value = response.data.data
    }

    pagination.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      total: response.data.total,
      has_more: response.data.current_page < response.data.last_page
    }
  } catch (error) {
    console.error('Failed to load comments', error)
  } finally {
    loading.value = false
  }
}

// Post a new comment
const postComment = async (parentId = null) => {
  if (!newComment.value.trim()) return

  submitting.value = true
  try {
    const response = await axios.post(teacher.content.comments.store(props.contentId).url, {
      body: newComment.value,
      parent_id: parentId,
      teacher_content_id: props.contentId
    })

    if (parentId) {
      // Find parent and append reply
      const parent = comments.value.find(c => c.id === parentId)
      if (parent) {
        if (!parent.replies) parent.replies = []
        parent.replies.push(response.data)
      }
      replyingTo.value = null
    } else {
      // Add to top of list
      comments.value.unshift(response.data)
      // Increment total count
      pagination.value.total++
    }

    newComment.value = ''
  } catch (error) {
    console.error('Failed to post comment', error)
  } finally {
    submitting.value = false
  }
}

// Delete a comment (replies are automatically deleted via database cascade)
const deleteComment = async (commentId, parentId = null) => {
  try {
    await axios.delete(teacher.content.comments.delete(commentId).url)

    if (parentId) {
      // Remove reply
      const parent = comments.value.find(c => c.id === parentId)
      if (parent && parent.replies) {
        parent.replies = parent.replies.filter(r => r.id !== commentId)
      }
    } else {
      // Remove root comment
      comments.value = comments.value.filter(c => c.id !== commentId)
      // Decrement total count
      pagination.value.total = Math.max(0, pagination.value.total - 1)
    }
  } catch (error) {
    console.error('Failed to delete comment', error)
  }
}

// Reply handler
const openReply = (commentId) => {
  replyingTo.value = commentId
  newComment.value = '' // Reset input when switching reply target
  // TODO: Focus input?
}

const cancelReply = () => {
  replyingTo.value = null
  newComment.value = ''
}

const loadMore = () => {
  if (pagination.value.has_more) {
    fetchComments(pagination.value.current_page + 1, true)
  }
}

onMounted(() => {
  fetchComments()
})
</script>

<template>
  <div class="space-y-8">
    <!-- Header -->
    <div class="flex items-center gap-2 mb-6">
        <span class="material-symbols-outlined text-2xl text-gray-600">comment</span>
        <h3 class="text-xl font-bold text-gray-900">Comments ({{ pagination.total ?? 0 }})</h3>
    </div>

    <!-- New Comment Input (Root) -->
    <div class="flex gap-4">
       <Avatar class="mt-1">
          <AvatarImage :src="currentUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=3B82F6&color=fff`" />
          <AvatarFallback>{{ currentUser.name.charAt(0) }}</AvatarFallback>
       </Avatar>
       <div class="flex-1 space-y-2">
          <Textarea 
             v-model="newComment" 
             placeholder="Write a comment..." 
             class="min-h-[100px] resize-none"
             :disabled="submitting || replyingTo !== null"
          />
          <div class="flex justify-end" v-if="replyingTo === null">
              <Button @click="postComment(null)" :disabled="submitting || !newComment.trim()">
                  {{ submitting ? 'Posting...' : 'Post Comment' }}
              </Button>
          </div>
       </div>
    </div>

    <!-- Comments List -->
    <div class="space-y-6">
        <div v-if="loading && comments.length === 0" class="text-center py-8 text-gray-500">
            Loading comments...
        </div>

        <div v-else-if="comments.length === 0" class="text-center py-8 bg-gray-50 rounded-xl text-gray-400">
             <div class="size-12 rounded-full bg-white flex items-center justify-center mx-auto mb-3 shadow-sm">
                 <span class="material-symbols-outlined text-2xl text-gray-300">chat_bubble_outline</span>
             </div>
             <p>No comments yet. Be the first to share your thoughts!</p>
        </div>

        <!-- Comment Item -->
        <div v-for="comment in comments" :key="comment.id" class="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div class="flex gap-4 group">
                <Avatar class="mt-1 size-10">
                    <AvatarImage :src="comment.user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.user.name)}&background=3B82F6&color=fff`" />
                    <AvatarFallback>{{ comment.user.name.charAt(0) }}</AvatarFallback>
                </Avatar>
                
                <div class="flex-1">
                    <!-- Comment Bubble -->
                    <div class="bg-gray-50 rounded-2xl rounded-tl-sm p-4 relative group-hover:bg-gray-100 transition-colors">
                        <div class="flex items-start justify-between mb-1">
                            <div>
                                <span class="font-bold text-sm text-gray-900 mr-2">{{ comment.user.name }}</span>
                                <span v-if="comment.user.is_author" class="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded font-medium">Author</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <span class="text-xs text-gray-500">{{ humanizeDate(comment.created_at) }}</span>
                                <!-- Delete Button -->
                                <AlertDialog v-if="canDeleteComment(comment)">
                                    <AlertDialogTrigger as-child>
                                        <button class="text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all">
                                            <span class="material-symbols-outlined text-lg">delete</span>
                                        </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Delete Comment?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                This action cannot be undone. This will permanently delete this comment.
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                            <AlertDialogAction @click="deleteComment(comment.id)">Delete</AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            </div>
                        </div>
                        <p class="text-gray-700 text-sm whitespace-pre-wrap">{{ comment.body }}</p>
                    </div>

                    <!-- Actions -->
                    <div class="flex items-center gap-4 mt-1 ml-2">
                        <button @click="openReply(comment.id)" class="text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors">Reply</button>
                    </div>

                    <!-- Reply Input -->
                    <div v-if="replyingTo === comment.id" class="mt-4 flex gap-3 animate-in fade-in zoom-in-95 duration-200">
                         <Avatar class="size-8">
                            <AvatarImage :src="currentUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.name)}&background=3B82F6&color=fff`" />
                            <AvatarFallback>{{ currentUser.name.charAt(0) }}</AvatarFallback>
                         </Avatar>
                         <div class="flex-1 space-y-2">
                            <Textarea 
                               v-model="newComment" 
                               placeholder="Write a reply..." 
                               class="min-h-[80px] text-sm resize-none"
                               auto-focus
                            />
                            <div class="flex justify-end gap-2">
                                <Button variant="ghost" size="sm" @click="cancelReply">Cancel</Button>
                                <Button size="sm" @click="postComment(comment.id)" :disabled="submitting || !newComment.trim()">
                                    {{ submitting ? 'Posting...' : 'Reply' }}
                                </Button>
                            </div>
                         </div>
                    </div>

                    <!-- Replies List -->
                    <div v-if="comment.replies && comment.replies.length > 0" class="mt-4 space-y-4 pl-4 border-l-2 border-gray-100 ml-4">
                        <div v-for="reply in comment.replies" :key="reply.id" class="flex gap-3 group/reply">
                            <Avatar class="mt-1 size-8">
                                <AvatarImage :src="reply.user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(reply.user.name)}&background=3B82F6&color=fff`" />
                                <AvatarFallback>{{ reply.user.name.charAt(0) }}</AvatarFallback>
                            </Avatar>
                            <div class="flex-1">
                                <div class="bg-gray-50 rounded-2xl rounded-tl-sm p-3 relative group-hover/reply:bg-gray-100 transition-colors">
                                    <div class="flex items-start justify-between mb-1">
                                        <div>
                                            <span class="font-bold text-xs text-gray-900 mr-2">{{ reply.user.name }}</span>
                                            <span v-if="reply.user.is_author" class="bg-blue-100 text-blue-700 text-[10px] px-1.5 py-0.5 rounded font-medium">Author</span>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span class="text-[10px] text-gray-500">{{ humanizeDate(reply.created_at) }}</span>
                                            <!-- Delete Button -->
                                            <AlertDialog v-if="canDeleteComment(reply)">
                                                <AlertDialogTrigger as-child>
                                                    <button class="text-gray-400 hover:text-red-600 opacity-0 group-hover/reply:opacity-100 transition-all">
                                                        <span class="material-symbols-outlined text-base">delete</span>
                                                    </button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete Reply?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This action cannot be undone. This will permanently delete this reply.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction @click="deleteComment(reply.id, comment.id)">Delete</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </div>
                                    </div>
                                    <p class="text-gray-700 text-sm whitespace-pre-wrap">{{ reply.body }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Load More -->
        <div v-if="pagination.has_more" class="text-center pt-4">
            <Button variant="outline" @click="loadMore" :disabled="loading">
                {{ loading ? 'Loading...' : 'Load more comments' }}
            </Button>
        </div>
    </div>
  </div>
</template>
